<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<template>
	<div v-if="$slots.header">
		<div class="mb-6">
			<slot name="header" :move="move"></slot>
		</div>

		<!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
		<div
			ref="scrollable"
			v-bind="$attrs"
			class="relative scrollbar-thin scrollbar-thumb-black scrollbar-track-gray-100 overflow-auto scroll-smooth scrollbar-thumb-rounded-full scrollbar-track-rounded-full"
			:class="{
				'scrollbar-none!': noScrollbar,
				'pb-sm': !noScrollbar,
			}"
			@mousedown="mouseIsDown"
			@mouseup="mouseUp"
			@click.capture="onClick"
			@mouseleave="mouseLeave"
			@mousemove="mouseMove"
		>
			<slot />
		</div>
	</div>

	<!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
	<div
		v-else
		ref="scrollable"
		v-bind="$attrs"
		class="relative scrollbar-thin scrollbar-thumb-black scrollbar-track-gray-100 overflow-auto scroll-smooth scrollbar-thumb-rounded-full scrollbar-track-rounded-full"
		:class="{
			'scrollbar-none!': noScrollbar,
			'pb-sm': !noScrollbar,
		}"
		@mousedown="mouseIsDown"
		@mouseup="mouseUp"
		@click.capture="onClick"
		@mouseleave="mouseLeave"
		@mousemove="mouseMove"
	>
		<slot />
	</div>
</template>

<script lang="ts" setup>
import {commonMobileRegex} from "@pkg/shared/helpers/user-agents";

defineProps<{
	noScrollbar?: boolean;
}>();

const scrollable = ref<HTMLDivElement>();

// This is only used for foolproofing that we don't enable our custom click-to-drag functionality on mobile
const isMobile = ref(false);

function move(direction: number) {
	const width =
		scrollable.value?.querySelector(".scrollable-item:first-child")
			?.clientWidth || 0;

	scrollable.value?.scroll({
		left: scrollable.value.scrollLeft + (direction > 0 ? width : -width),
		behavior: "smooth",
	});
}

// The logic below is used for implementing a custom feature that allows "dragging" with a mouse to scroll the container. This is useful for mouse users whose mouse doesn't have a horizontal scroll wheel.

const startX = ref<number>(0);
const scrollLeft = ref<number>(0);
const isDown = ref<boolean>(false);
const isMoving = ref<boolean>(false);

function mouseIsDown(event: MouseEvent) {
	// Foolproofing, this event normally doesn't happen on mobile
	if (isMobile.value) {
		return;
	}

	if (!scrollable.value) {
		return;
	}

	event.preventDefault();

	isDown.value = true;
	startX.value = event.pageX - scrollable.value.offsetLeft;
	scrollLeft.value = scrollable.value?.scrollLeft;
}

function mouseUp(_event: MouseEvent) {
	// Foolproofing, this event normally doesn't happen on mobile
	if (isMobile.value) {
		return;
	}

	isDown.value = false;
	// Delay updating this until the click handler has time to fire
	setTimeout(() => {
		isMoving.value = false;
	}, 0);
}

function mouseLeave() {
	// Foolproofing, this event normally doesn't happen on mobile
	if (isMobile.value) {
		return;
	}

	isDown.value = false;
	isMoving.value = false;
}

function mouseMove(event: MouseEvent) {
	// Foolproofing, this event normally doesn't happen on mobile
	if (isMobile.value) {
		return;
	}

	if (!scrollable.value || !isDown.value) {
		return;
	}

	isMoving.value = true;

	// Scroll the container by an amount that corresponds to the distance the mouse moved since it was pressed down
	const x = event.pageX - scrollable.value.offsetLeft;
	const walkX = x - startX.value;
	scrollable.value.scrollLeft = scrollLeft.value - walkX;
}

// This event should be captured (handled by this before any inner elements), and it prevents inner links for example from triggering if a drag is going on.
function onClick(event: MouseEvent) {
	// This should always be false on mobile devices and on desktop devices if the user currently isn't dragging
	if (!isMoving.value) {
		return;
	}

	event.preventDefault();
	event.stopPropagation();
}

onMounted(() => {
	isMobile.value = commonMobileRegex.test(navigator.userAgent);
});
</script>

<script lang="ts">
export default {
	inheritAttrs: false,
};
</script>
